import React, { useCallback, useState } from "react";
import { Trans, useTranslation } from "react-i18next";
import { Button, Body2, Body1, ConfirmDialog } from "@activecollab/components";
import classNames from "classnames";
import PropTypes from "prop-types";

import { FormatLocalDateTime } from "~/components/Formatters";
import UserNameContainer from "~/components/Users/TaskAssignee";
import Angie from "~/utils/angie";

export const TrashSlate = ({
  type,
  canPermanentlyDelete,
  canRestore,
  createdByName,
  createdById,
  createdOn,
  trashedById,
  trashedOn,
  children,
  isTrashed,
  title,
  className,
  subtitle,
  onRestore,
  onPermanentlyDelete,
}) => {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const objectType = type?.toLowerCase();

  const handleClose = useCallback(() => {
    setOpen(false);
  }, []);

  const handleOpen = useCallback(() => {
    setOpen(true);
  }, []);

  const handlePermanentlyDelete = useCallback(() => {
    handleClose();
    typeof onPermanentlyDelete === "function" && onPermanentlyDelete();
  }, [handleClose, onPermanentlyDelete]);

  if (!isTrashed) {
    return children;
  }
  return (
    <div className={classNames("tw-flex tw-justify-center tw-flex-col", className)}>
      {title && (
        <Body1
          align="center"
          color="secondary"
          className="tw-mx-6"
        >
          {title}
        </Body1>
      )}

      <div className="tw-text-center tw-mb-12">
        <img
          src={`${Angie.assets_url}/environment/images/layout/trash_empty_page.png`}
          alt="Trash"
          width={250}
          height={250}
        />
        {subtitle}
      </div>

      <div className="tw-mb-8">
        <Body2
          color="tertiary"
          className="tw-mb-2"
        >
          <Body2
            color="secondary"
            className="tw-inline"
          >
            {t("Created")}:
          </Body2>{" "}
          {createdOn ? <FormatLocalDateTime value={createdOn} /> : t("Unknown")} {t("by")}{" "}
          {createdById ? <UserNameContainer userId={createdById} /> : createdByName ? createdByName : t("Unknown")}
        </Body2>
        <Body2 color="tertiary">
          <Body2
            color="secondary"
            className="tw-inline"
          >
            {t("Deleted")}:
          </Body2>{" "}
          <span>{trashedOn ? <FormatLocalDateTime value={trashedOn} /> : t("Unknown")}</span> {t("by")}{" "}
          {trashedById ? <UserNameContainer userId={trashedById} /> : t("Unknown")}
        </Body2>
      </div>
      <div className="tw-flex tw-flex-col tw-text-center tw-items-center">
        {canRestore && (
          <Button
            variant="contained"
            onClick={onRestore}
          >
            {t("Recover")}
          </Button>
        )}
        {canPermanentlyDelete && (
          <Body2
            decoration="underline"
            color="tertiary"
            onClick={handleOpen}
            className="tw-mt-3 tw-cursor-pointer"
          >
            {t("Remove permanently")}
          </Body2>
        )}
      </div>
      <ConfirmDialog
        open={open}
        onCancel={handleClose}
        onConfirm={handlePermanentlyDelete}
        dialogTitle={<Trans>Remove {objectType} permanently</Trans>}
        dialogContent={
          <Trans>
            The {objectType} will be permanently removed from the account, and you won’t be able to access it anymore.
          </Trans>
        }
        confirmBtnText={t("Delete")}
        cancelBtnText={t("Cancel")}
      />
    </div>
  );
};

TrashSlate.displayName = "TrashSlate";

TrashSlate.propTypes = {
  type: PropTypes.string.isRequired,
  title: PropTypes.node,
  isTrashed: PropTypes.bool,
  createdOn: PropTypes.number,
  createdById: PropTypes.number,
  createdByName: PropTypes.string,
  trashedOn: PropTypes.number,
  trashedById: PropTypes.number,
  canRestore: PropTypes.bool,
  canPermanentlyDelete: PropTypes.bool,
  onRestore: PropTypes.func,
  onPermanentlyDelete: PropTypes.func,
  children: PropTypes.node.isRequired,
  subtitle: PropTypes.node.isRequired,
  className: PropTypes.string,
};

TrashSlate.defaultProps = {
  canRestore: false,
  canPermanentlyDelete: false,
  isTrashed: false,
};
